import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import { blockPlayer, getBlockStatus, unblockPlayer } from '../../../services/blocksApi';
import { showPlainToast } from '../../../utils/helpers/toastUtils';
import { ACHIEVEMENTS_FONT, achievementsColors } from '../Achievements/achievementsTheme';
import { Pressable } from '../common/Touchable';
import { ChatBubbleIcon, NoEntryIcon, ProfileActionButton } from './ProfileActionButton';

type Props = {
	playerId: string;
	username: string;
	scale: number;
	onOpenChat: () => void;
};

/**
 * Another player's profile once a donation between the two has unlocked their private chat:
 * BLOCK CHAT and PRIVATE CHAT side by side. Blocking asks first, in place of the two buttons,
 * because it also stops donations and messages both ways; unblocking does not. Until the block
 * state loads, Block shows its spinner so it never offers the wrong action.
 */
export const ProfileChatActions = ({ playerId, username, scale, onOpenChat }: Props) => {
	const { t } = useTranslation();
	const styles = useMemo(() => createStyles(scale), [scale]);
	/** Null until `GET /player/blocks/:id` answers. */
	const [blockedByMe, setBlockedByMe] = useState<boolean | null>(null);
	const [confirming, setConfirming] = useState(false);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let current = true;
		setBlockedByMe(null);
		getBlockStatus(playerId)
			.then(status => {
				if (current) setBlockedByMe(status.blockedByMe);
			})
			.catch(error =>
				console.warn('[ProfileChatActions] block status unavailable', error?.message),
			);
		return () => {
			current = false;
		};
	}, [playerId]);

	const setBlocked = async (block: boolean) => {
		setBusy(true);
		try {
			const status = block ? await blockPlayer(playerId) : await unblockPlayer(playerId);
			setBlockedByMe(status.blockedByMe);
			setConfirming(false);
			showPlainToast(
				'success',
				block
					? t('T_SITE_BLOCK_DONE', { defaultValue: 'Player blocked' })
					: t('T_SITE_BLOCK_UNBLOCK_DONE', { defaultValue: 'Player unblocked' }),
			);
		} catch {
			showPlainToast(
				'error',
				t('T_SITE_BLOCK_FAILED', { defaultValue: 'Something went wrong. Please try again.' }),
			);
		} finally {
			setBusy(false);
		}
	};

	if (confirming) {
		return (
			<View style={styles.confirm}>
				<Text style={styles.confirmText}>
					{t('T_SITE_BLOCK_CONFIRM', {
						defaultValue:
							"Block {{username}}? They won't be able to message you or send you donations, and you won't be able to message or donate to them.",
						username,
					})}
				</Text>
				<View style={styles.confirmActions}>
					<Pressable
						accessibilityRole='button'
						onPress={busy ? undefined : () => setConfirming(false)}
						style={styles.cancel}
					>
						<Text numberOfLines={1} style={styles.cancelLabel}>
							{t('T_Cancel', { defaultValue: 'Cancel' })}
						</Text>
					</Pressable>
					<ProfileActionButton
						label={t('T_SITE_BLOCK_CONFIRM_BUTTON', { defaultValue: 'Block' })}
						icon={NoEntryIcon}
						variant='red'
						scale={scale}
						busy={busy}
						onPress={() => setBlocked(true)}
					/>
				</View>
			</View>
		);
	}

	return (
		<View style={styles.row}>
			<ProfileActionButton
				label={
					blockedByMe
						? t('T_SITE_CHAT_UNBLOCK_CHAT', { defaultValue: 'Unblock chat' })
						: t('T_SITE_CHAT_BLOCK_CHAT', { defaultValue: 'Block chat' })
				}
				icon={NoEntryIcon}
				variant='red'
				scale={scale}
				busy={busy || blockedByMe === null}
				onPress={() => (blockedByMe ? setBlocked(false) : setConfirming(true))}
			/>
			<ProfileActionButton
				label={t('T_SITE_MESSAGE_CENTER_PRIVATE_CHAT', { defaultValue: 'Private Chat' })}
				icon={ChatBubbleIcon}
				variant='gold'
				scale={scale}
				onPress={onOpenChat}
			/>
		</View>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;
	return StyleSheet.create({
		row: { flexDirection: 'row', columnGap: s(12) },
		confirm: {
			rowGap: s(10),
			padding: s(12),
			borderRadius: s(10),
			borderWidth: Math.max(1, s(1)),
			borderColor: '#FF4D3D',
			backgroundColor: achievementsColors.overlay,
		},
		confirmText: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(13),
			lineHeight: s(17),
			fontWeight: '600',
			textAlign: 'center',
		},
		confirmActions: { flexDirection: 'row', columnGap: s(12) },
		cancel: {
			flex: 1,
			height: s(44),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(10),
			borderWidth: Math.max(1, s(1)),
			borderColor: achievementsColors.panelOrange,
		},
		cancelLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(15),
			fontWeight: '700',
		},
	});
};

export default ProfileChatActions;
